import * as React from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { useEffect } from 'react';
import { IoMdClose } from 'react-icons/io';
import { Helmet } from 'react-helmet';
import { ManagedOverlayProps } from '@/features/common-overlays/overlay-manager';
import { breakpoints, isMobile } from '@/components/responsive';
import { VisuallyLogo } from '@/features/strapi/components/logo';
import {
  HubspotField,
  HubspotForm,
} from '@/features/new-landing-page/components/hubspot-form';
import { FORM_CSS } from '@/features/new-landing-page/components/book-demo-overlay';
import { useDetachedState } from '@/components/hooks/use-detached-state';
import { VSpace } from '@/components/spacing';
import { useLoggedInAccount } from '@/features/account-context';

export type ReferralOverlayProps = ManagedOverlayProps;

export function ReferAFriendOverlay({ onClose }: ReferralOverlayProps) {
  const {
    store: { email },
  } = useLoggedInAccount();
  const [formRefState, setFormRef, formRef] =
    useDetachedState<HTMLFormElement>(null);

  const isMobileDevice = isMobile();

  useEffect(() => {
    if (formRef?.current && email) {
      const emailInput = formRef.current.querySelector(
        `input[name="email"]`,
      ) as HTMLInputElement;
      if (emailInput) {
        emailInput.value = email;
        emailInput.dispatchEvent(new Event(`change`, { bubbles: true }));
        emailInput.dispatchEvent(new Event(`input`, { bubbles: true }));
      }
    }
  }, [email, formRefState]);

  const handleSubmit = (_1: HTMLFormElement, _2: HubspotField[]) => {
    setTimeout(onClose, 3000);
  };

  return (
    <Wrapper
      initial={{ scale: 1.5, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 1.5, opacity: 0 }}
    >
      <Helmet>
        <script
          charSet="utf-8"
          type="text/javascript"
          src="//js-eu1.hsforms.net/forms/embed/v2.js"
        />
      </Helmet>
      <IoMdClose className="close-btn" size={35} onClick={onClose} />
      <Left>
        <ContentWrapper>
          <VSpace value={1} className="only-mobile" />
          <VisuallyLogo wide />
          <Title>Share Visually with a brand you ❤️</Title>
          <SubTitle>
            Introduce us to a store that could benefit from Visually.io, and as
            a thank you you&apos;ll get a gift card you can redeem with 2,000+
            options (including Amazon and PayPal) when things move forward.
          </SubTitle>
          <VSpace value={0.1} className="only-mobile" />
          <div className="only-mobile" style={{ width: `100%` }}>
            <Card>
              <div className="header">
                <div className="title">3K-10K Monthly Orders</div>
                <div className="total"> Total: $800</div>
              </div>
              <Separator />
              <div>$300 after a demo</div>
              <div>+$500 if converted into a customer</div>
            </Card>

            <VSpace value={3} />

            <Card>
              <div className="header">
                <div className="title">10K+ Monthly Orders</div>
                <div className="total"> Total: $1500</div>
              </div>
              <Separator />
              <div>$300 after a demo</div>
              <div>+$1200 if converted into a customer</div>
            </Card>
          </div>
          <FormWrapper className="only-desktop">
            {!isMobileDevice && (
              <HubspotForm
                formId="f63d6e2a-e95c-4d35-9aa3-746a21cbe531"
                portalId="143410811"
                submitText="Submit"
                onFormReady={(form) => {
                  setFormRef(form);
                }}
                onFormSubmit={handleSubmit}
                additionalCss={FORM_CSS}
              />
            )}
          </FormWrapper>
        </ContentWrapper>
      </Left>
      <Right>
        <ContentWrapper>
          <FormWrapper className="only-mobile">
            {isMobileDevice && (
              <HubspotForm
                formId="f63d6e2a-e95c-4d35-9aa3-746a21cbe531"
                portalId="143410811"
                submitText="Submit"
                onFormReady={(form) => {
                  setFormRef(form);
                }}
                onFormSubmit={handleSubmit}
                additionalCss={FORM_CSS}
              />
            )}
          </FormWrapper>
          <Title className="only-desktop">How it works</Title>
          <StepWrapper className="only-desktop">
            <Step>
              <StepOrdinal>1</StepOrdinal>
              Submit a brand
            </Step>
            <Step>
              <StepOrdinal>2</StepOrdinal>
              We&apos;ll book demo
            </Step>
            <Step>
              <StepOrdinal>3</StepOrdinal>
              You get paid
            </Step>
          </StepWrapper>

          <VSpace value={1} className="only-desktop" />
          <Title className="only-desktop">Rewards</Title>
          <div className="only-desktop" style={{ width: `80%` }}>
            <Card>
              <div className="header">
                <div className="title">3K-10K Monthly Orders</div>
                <div className="total"> Total: $800</div>
              </div>
              <Separator />
              <div>$300 after a demo</div>
              <div>+$500 if converted into a customer</div>
            </Card>

            <VSpace value={3} />

            <Card>
              <div className="header">
                <div className="title">10K+ Monthly Orders</div>
                <div className="total"> Total: $1500</div>
              </div>
              <Separator />
              <div>$300 after a demo</div>
              <div>+$1200 if converted into a customer</div>
            </Card>
          </div>
        </ContentWrapper>
      </Right>
    </Wrapper>
  );
}

const Wrapper = styled(motion.div)`
  && {
    position: fixed;
    inset: 0;
    background: white;
    height: 100vh;
    width: 100vw;
    z-index: 1000000;

    display: grid;
    grid-template-columns: 1fr 1fr;

    ${breakpoints.down(`md`)} {
      grid-template-columns: 1fr;
      overflow-y: scroll;
    }

    .only-desktop {
      ${breakpoints.down(`md`)} {
        display: none !important;
      }
    }

    .only-mobile {
      ${breakpoints.up(`md`)} {
        display: none !important;
      }
    }

    .close-btn {
      position: absolute;
      top: 4rem;
      right: 4rem;
      cursor: pointer;
      transition: transform 0.2s;
    }

    .close-btn:hover {
      transform: scale(1.1);
    }
  }
`;

const Left = styled.div`
  background: white;

  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  ${breakpoints.down(`md`)} {
    background: #f6f5f4;
    padding: 8vh 0;
  }
`;

const Right = styled.div`
  background: #f6f5f4;
  display: flex;
  flex-direction: column;

  justify-content: center;
  align-items: center;

  width: 100%;

  ${breakpoints.down(`md`)} {
    padding: 1vh 0;
    background: white;
  }
`;

const Title = styled.span`
  font-size: 3rem;
  font-family: 'Eesti', serif;
  font-weight: bold;
  line-height: 1.5;
`;

const SubTitle = styled.span`
  font-size: 1.5rem;
  font-family: 'Eesti', serif;
  font-weight: normal;
  line-height: 1.5;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;

  min-width: 65%;
  max-width: 65%;

  gap: 2rem;

  && * {
    user-select: none;
  }

  .strapi-textual {
    text-align: start;
  }

  .benefits {
    margin-top: -1rem;
  }

  ${breakpoints.down(`md`)} {
    min-width: 100%;
    max-width: 100%;

    padding: 0 10vw;

    .benefits {
      margin-left: -1rem;
    }
  }
`;

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: 1rem;
  min-height: 40rem;

  .hubspot-form {
    width: 100%;
  }

  ${breakpoints.down(`md`)} {
    padding-top: 3rem;
    padding-bottom: 10rem;
  }
`;

const StepWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 2rem;
`;

const Step = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 1.5rem;

  font-family: 'Eesti', serif;
  font-size: 1.4rem;
`;

const StepOrdinal = styled.div`
  font-size: 2rem;
  font-family: Inter, serif;
  font-weight: normal;
  background: white;
  height: 3.5rem;
  width: 3.5rem;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  text-align: center;
  border-radius: 50%;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0),
    0 5px 7px 0 rgba(151, 210, 206, 0.22), 0 1px 1px 0 rgba(0, 0, 0, 0.15);
`;

const Card = styled.div`
  background: white;
  border-radius: 2rem;
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0),
    0 5px 7px 0 rgba(151, 210, 206, 0.22), 0 1px 1px 0 rgba(0, 0, 0, 0.15);
  padding: 3rem;

  display: flex;
  flex-direction: column;
  gap: 2rem;

  font-family: 'Eesti', serif;

  font-size: 1.4rem;

  width: 100%;

  .header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    align-items: center;
  }

  .title {
    font-size: 1.6rem;
    font-weight: bold;

    ${breakpoints.down(`md`)} {
      font-size: 1.4rem;
    }
  }

  .total {
    font-size: 1.5rem;
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background: #e0e0e0;
`;
